// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    try {
      return savedUser && savedUser !== "undefined" ? JSON.parse(savedUser) : null;
    } catch (e) {
      return null;
    }
  });
  const [token, setToken] = useState(() => {
    const savedToken = localStorage.getItem('token');
    if (!savedToken) return null;
    // Limpiar posibles comillas si se guardó incorrectamente como JSON string
    return savedToken.replace(/^["'](.+)["']$/, '$1');
  });
  const [permissions, setPermissions] = useState({});
  const [authLoading, setAuthLoading] = useState(true);
  const [sessionLimit, setSessionLimit] = useState(30 * 60 * 1000); // 30m default

  // Immediate initialization of axios headers for those already logged in
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.defaults.headers.common['X-Authorization'] = `Bearer ${token}`;
  }

  const can = useCallback((modulo, accion = 'acceso') => {
    if (user?.rol_id === 1) return true;
    if (!permissions[modulo]) return false;
    return !!permissions[modulo][accion];
  }, [user, permissions]);

  const logout = useCallback(async () => {
    const currentToken = token;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Authorization'];
    setToken(null);
    setUser(null);
    setPermissions({});

    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/frontend/login';
    }

    if (currentToken) {
      try {
        await axios.post('/backend/api/auth/logout.php');
      } catch (e) {
        console.warn('Logout server-side fallido');
      }
    }
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/backend/api/auth/login.php', { email, password });
      if (response.data.success) {
        const { token, empleado } = response.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(empleado));
        
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        axios.defaults.headers.common['X-Authorization'] = `Bearer ${token}`;
        
        setToken(token);
        setUser(empleado);
        await fetchPermissions(token);
        
        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Error de conexión' };
    }
  };

  const fetchPermissions = useCallback(async (tokenToUse = token) => {
    if (!tokenToUse) {
      setPermissions({});
      setAuthLoading(false);
      return;
    }
    try {
      const response = await axios.get('/backend/api/auth/get_permissions.php', {
        headers: {
          'Authorization': `Bearer ${tokenToUse}`,
          'X-Authorization': `Bearer ${tokenToUse}`
        }
      });
      if (response.data.success) {
        setPermissions(response.data.data.permisos);
      }
    } catch (error) {
      const serverMessage = error.response?.data?.message || error.message;
      console.error('Error al cargar la malla de seguridad:', serverMessage, error.response?.data);
    } finally {
      setAuthLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setAuthLoading(false);
      return;
    }

    fetchPermissions();
    
    axios.get('/backend/api/configuracion/index.php')
      .then(res => {
        if (res.data.success && res.data.data.auto_logout_minutos) {
          const mins = parseInt(res.data.data.auto_logout_minutos);
          if (mins > 0) setSessionLimit(mins * 60 * 1000);
        }
      })
      .catch(() => {
        console.warn('No se pudo cargar la configuración operativa (posible sesión expirada)');
      });
  }, [token, fetchPermissions]);

  useEffect(() => {
    if (!token) return;

    let logoutTimer;
    const resetTimer = () => {
      if (logoutTimer) clearTimeout(logoutTimer);
      logoutTimer = setTimeout(() => {
        logout();
      }, sessionLimit);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(name => document.addEventListener(name, resetTimer));
    resetTimer();

    return () => {
      events.forEach(name => document.removeEventListener(name, resetTimer));
      if (logoutTimer) clearTimeout(logoutTimer);
    };
  }, [token, logout, sessionLimit]);

  useEffect(() => {
    const reqInterceptor = axios.interceptors.request.use((config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        config.headers['X-Authorization'] = `Bearer ${token}`;
      }
      return config;
    }, (error) => {
      return Promise.reject(error);
    });

    const resInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // ERROR 401: Sesión Expirada o no autorizada
        if (error.response && error.response.status === 401) {
          // Limpiar datos pero evitar bucle infinito de redirección
          const isLoginPage = window.location.pathname.includes('/login');
          
          if (!isLoginPage) {
            logout(); // logout() ahora tiene window.location.href = '/login'
          } else {
            // Si ya estamos en login y da 401, solo limpiamos estado local silenciosamente
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
          
          // Silenciamos el error para que no aparezca el overlay de React en dev
          return new Promise(() => {});
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, [token, logout]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      permissions, 
      can, 
      authLoading,
      login, 
      logout, 
      isAuthenticated: !!token 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
